import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GraduationCap,
  Building2,
  Library,
  BookOpen,
  FileText,
  PanelLeftClose,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { ScrollArea } from 'components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

const SIDEBAR_SECTIONS = [
  {
    title: 'Favorites',
    items: [
      { key: 'fav-researchers', label: 'Researchers', icon: GraduationCap },
      { key: 'fav-units', label: 'Units', icon: Library },
      { key: 'fav-institutions', label: 'Institutions', icon: Building2 },
      { key: 'fav-venues', label: 'Venues', icon: BookOpen },
    ],
  },
  {
    title: 'Saved Searches',
    items: [
      { key: 'saved-researchers', label: 'Researcher Searches', icon: GraduationCap },
      { key: 'saved-units', label: 'Unit Searches', icon: Library },
      { key: 'saved-institutions', label: 'Institution Searches', icon: Building2 },
      { key: 'saved-venues', label: 'Venue Searches', icon: BookOpen },
    ],
  },
  {
    title: 'Reports',
    items: [
      { key: 'reports', label: 'Reports & Exports', icon: FileText },
    ],
  },
];

const LibrarySidebar = ({ collapsed, onToggle }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'fav-researchers';
  const isReportActive = !activeTab.startsWith('fav-') && !activeTab.startsWith('saved-');

  const onTabChange = (key) => {
    if (key === 'reports') {
      if (!isReportActive) {
        setSearchParams({ tab: 'collaboration' });
      }
      return;
    }
    if (key) {
      setSearchParams({ tab: key });
    }
  };

  return (
    <aside
      className={cn(
        collapsed
          ? 'w-0 border-none opacity-0 overflow-hidden px-0'
          : 'w-[260px] opacity-100',
        'flex flex-col bg-white border-r border-gray-200 text-gray-700 h-full flex-shrink-0 transition-all duration-300 relative select-none'
      )}
    >
      <TooltipProvider delayDuration={0}>
        {/* Header - height 60px matching main sidebar */}
        <div className="flex h-[60px] items-center justify-between px-4 shrink-0 min-w-[260px]">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="font-semibold text-gray-900 truncate">My Library</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              >
                <PanelLeftClose size={18} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="z-[9999] bg-gray-900 text-white border-none shadow-lg py-1 px-2.5 text-xs"
            >
              Collapse sidebar
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Scrollable Navigation Body */}
        <ScrollArea className="flex-1 px-3 py-2 min-w-[260px]">
          <div className="flex flex-col gap-6">
            {SIDEBAR_SECTIONS.map((section, idx) => (
              <div key={idx} className="flex flex-col gap-1">
                <span className="px-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 mt-1">
                  {section.title}
                </span>

                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.key === 'reports' ? isReportActive : activeTab === item.key;

                  return (
                    <Button
                      key={item.key}
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start gap-3 h-9 text-sm font-normal',
                        isActive
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                      )}
                      onClick={() => onTabChange(item.key)}
                    >
                      <div className="relative">
                        <Icon size={18} />
                      </div>
                      <span className="truncate">{item.label}</span>
                      {item.badge && (
                        <Badge
                          variant="secondary"
                          className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-gray-100 text-gray-500 font-normal border border-gray-200"
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </div>
            ))}
          </div>
        </ScrollArea>
      </TooltipProvider>
    </aside>
  );
};

export default LibrarySidebar;
